import React, { useState, useCallback } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Card,
    Tooltip,
    useTheme,
    alpha,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import {
    CloudUpload,
    Delete,
    Star,
    StarBorder,
    AspectRatio,
    FitScreen,
    CenterFocusStrong,
    OpenWith,
    GridOn,
    Check
} from '@mui/icons-material';
import { MAX_PRODUCT_IMAGES, ALLOWED_IMAGE_TYPES, MAX_IMAGE_SIZE_MB } from '../config/constants';

export interface ImageFile {
    id: string;
    file: File | null;
    preview: string;
    isThumbnail: boolean;
    isExisting: boolean;
    resizeMode?: 'cover' | 'contain' | 'center' | 'stretch' | 'repeat';
}

interface DragDropImageUploaderProps {
    images: ImageFile[];
    onChange: (images: ImageFile[]) => void;
    maxImages?: number;
}

const RESIZE_MODE_OPTIONS: { value: ImageFile['resizeMode']; label: string; icon: React.ReactNode; description: string }[] = [
    { value: 'cover', label: 'Cover', icon: <FitScreen fontSize='small' />, description: 'Fill & crop' },
    { value: 'contain', label: 'Contain', icon: <AspectRatio fontSize='small' />, description: 'Fit inside' },
    { value: 'center', label: 'Center', icon: <CenterFocusStrong fontSize='small' />, description: 'No scaling' },
    { value: 'stretch', label: 'Stretch', icon: <OpenWith fontSize='small' />, description: 'Stretch to fill' },
    { value: 'repeat', label: 'Repeat', icon: <GridOn fontSize='small' />, description: 'Tile pattern' }
];

export const DragDropImageUploader: React.FC<DragDropImageUploaderProps> = ({
    images,
    onChange,
    maxImages = MAX_PRODUCT_IMAGES
}) => {
    const theme = useTheme();
    const [isDragging, setIsDragging] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [resizeModeAnchor, setResizeModeAnchor] = useState<{ element: HTMLElement; imageId: string } | null>(null);

    const validateFile = (file: File): string | null => {
        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            return `Invalid file type. Allowed types: PNG, JPG, JPEG, GIF, WEBP`;
        }
        if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
            return `File size exceeds ${MAX_IMAGE_SIZE_MB}MB`;
        }
        return null;
    };

    const handleFiles = useCallback((files: FileList) => {
        setError(null);
        const currentCount = images.length;
        const availableSlots = maxImages - currentCount;

        if (availableSlots <= 0) {
            setError(`Maximum ${maxImages} images allowed`);
            return;
        }

        const filesToAdd = Array.from(files).slice(0, availableSlots);
        const newImages: ImageFile[] = [];

        filesToAdd.forEach((file) => {
            const validationError = validateFile(file);
            if (validationError) {
                setError(validationError);
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                const newImage: ImageFile = {
                    id: `${Date.now()}-${Math.random()}`,
                    file,
                    preview: reader.result as string,
                    isThumbnail: images.length === 0 && newImages.length === 0, // First image is thumbnail
                    isExisting: false,
                    resizeMode: 'cover' // Default resize mode
                };
                newImages.push(newImage);

                if (newImages.length === filesToAdd.length) {
                    onChange([...images, ...newImages]);
                }
            };
            reader.readAsDataURL(file);
        });
    }, [images, maxImages, onChange]);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const files = e.dataTransfer.files;
        if (files.length > 0) {
            handleFiles(files);
        }
    }, [handleFiles]);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            handleFiles(e.target.files);
        }
    };

    const handleSetThumbnail = (id: string) => {
        const updatedImages = images.map(img => ({
            ...img,
            isThumbnail: img.id === id
        }));
        onChange(updatedImages);
    };

    const handleDeleteImage = (id: string) => {
        const imageToDelete = images.find(img => img.id === id);
        const updatedImages = images.filter(img => img.id !== id);
        
        // If deleted image was thumbnail, set first remaining image as thumbnail
        if (imageToDelete?.isThumbnail && updatedImages.length > 0) {
            updatedImages[0].isThumbnail = true;
        }
        
        onChange(updatedImages);
    };

    const handleResizeModeClick = (event: React.MouseEvent<HTMLElement>, imageId: string) => {
        event.stopPropagation();
        setResizeModeAnchor({ element: event.currentTarget, imageId });
    };

    const handleResizeModeClose = () => {
        setResizeModeAnchor(null);
    };

    const handleResizeModeChange = (resizeMode: ImageFile['resizeMode']) => {
        if (!resizeModeAnchor) return;
        const updatedImages = images.map(img =>
            img.id === resizeModeAnchor.imageId ? { ...img, resizeMode } : img
        );
        onChange(updatedImages);
        handleResizeModeClose();
    };

    const getResizeModeIcon = (mode: ImageFile['resizeMode']) => {
        const option = RESIZE_MODE_OPTIONS.find(opt => opt.value === mode);
        return option?.icon || <FitScreen fontSize='small' />;
    };

    return (
        <Box>
            {/* Drag and Drop Zone */}
            <Box
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                sx={{
                    border: '2px dashed',
                    borderColor: isDragging 
                        ? theme.palette.primary.main 
                        : theme.palette.divider,
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    backgroundColor: isDragging 
                        ? alpha(theme.palette.primary.main, 0.05)
                        : theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.02)'
                        : 'rgba(0, 0, 0, 0.01)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    minHeight: 120,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 2,
                    '&:hover': {
                        borderColor: theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.primary.main, 0.03)
                    }
                }}
                onClick={() => document.getElementById('file-upload-input')?.click()}
            >
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        backgroundColor: theme.palette.primary.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 1.5
                    }}
                >
                    <CloudUpload sx={{ fontSize: 20, color: 'white' }} />
                </Box>
                
                <Typography 
                    variant='body2' 
                    sx={{ 
                        fontFamily: 'primaryFont', 
                        fontWeight: 600,
                        mb: 0.25
                    }}
                >
                    Upload images or click here
                </Typography>
                
                <Typography 
                    variant='caption' 
                    color='text.secondary'
                    sx={{ fontFamily: 'primaryFont', fontSize: '0.7rem' }}
                >
                    PNG, JPG, GIF, WEBP • Max {maxImages} images • {MAX_IMAGE_SIZE_MB}MB each
                </Typography>

                {images.length > 0 && (
                    <Typography 
                        variant='caption' 
                        sx={{ 
                            fontFamily: 'primaryFont', 
                            mt: 1,
                            color: theme.palette.primary.main,
                            fontWeight: 600,
                            fontSize: '0.7rem'
                        }}
                    >
                        {images.length} of {maxImages} uploaded
                    </Typography>
                )}
            </Box>

            <input
                id='file-upload-input'
                type='file'
                multiple
                accept={ALLOWED_IMAGE_TYPES.join(',')}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
            />

            {error && (
                <Typography 
                    variant='caption' 
                    color='error'
                    sx={{ 
                        display: 'block',
                        mt: 1,
                        fontFamily: 'primaryFont'
                    }}
                >
                    {error}
                </Typography>
            )}

            {/* Image Previews */}
            {images.length > 0 && (
                <Box sx={{ mt: 3 }}>
                    <Typography 
                        variant='subtitle2' 
                        sx={{ 
                            fontFamily: 'primaryFont', 
                            fontWeight: 'bold',
                            mb: 2
                        }}
                    >
                        Uploaded Images
                    </Typography>
                    
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: 'repeat(1, 1fr)',
                                sm: 'repeat(2, 1fr)',
                                md: 'repeat(3, 1fr)'
                            },
                            gap: 2
                        }}
                    >
                        {images.map((image) => (
                            <Card
                                key={image.id}
                                variant='outlined'
                                sx={{
                                    position: 'relative',
                                    aspectRatio: '4/3',
                                    overflow: 'hidden',
                                    backgroundColor: 'white',
                                    border: image.isThumbnail 
                                        ? `3px solid ${theme.palette.primary.main}`
                                        : undefined,
                                    '&:hover .action-buttons': {
                                        opacity: 1
                                    }
                                }}
                            >
                                <img
                                    src={image.preview}
                                    alt='Product'
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain'
                                    }}
                                />
                                
                                {/* Action Buttons */}
                                <Box
                                    className='action-buttons'
                                    sx={{
                                        position: 'absolute',
                                        top: 4,
                                        right: 4,
                                        display: 'flex',
                                        gap: 0.5,
                                        opacity: 0,
                                        transition: 'opacity 0.2s'
                                    }}
                                >
                                    <Tooltip title={image.isThumbnail ? 'Thumbnail' : 'Set as Thumbnail'}>
                                        <IconButton
                                            size='small'
                                            onClick={() => handleSetThumbnail(image.id)}
                                            sx={{
                                                backgroundColor: image.isThumbnail 
                                                    ? theme.palette.primary.main 
                                                    : 'rgba(0,0,0,0.6)',
                                                color: 'white',
                                                '&:hover': {
                                                    backgroundColor: image.isThumbnail 
                                                        ? theme.palette.primary.dark 
                                                        : 'rgba(0,0,0,0.8)'
                                                }
                                            }}
                                        >
                                            {image.isThumbnail ? (
                                                <Star fontSize='small' />
                                            ) : (
                                                <StarBorder fontSize='small' />
                                            )}
                                        </IconButton>
                                    </Tooltip>

                                    <Tooltip title={`Resize: ${image.resizeMode || 'cover'}`}>
                                        <IconButton
                                            size='small'
                                            onClick={(e) => handleResizeModeClick(e, image.id)}
                                            sx={{
                                                backgroundColor: 'rgba(0,0,0,0.6)',
                                                color: 'white',
                                                '&:hover': {
                                                    backgroundColor: theme.palette.info.main
                                                }
                                            }}
                                        >
                                            {getResizeModeIcon(image.resizeMode)}
                                        </IconButton>
                                    </Tooltip>
                                    
                                    <Tooltip title='Delete'>
                                        <IconButton
                                            size='small'
                                            onClick={() => handleDeleteImage(image.id)}
                                            sx={{
                                                backgroundColor: 'rgba(0,0,0,0.6)',
                                                color: 'white',
                                                '&:hover': {
                                                    backgroundColor: theme.palette.error.main
                                                }
                                            }}
                                        >
                                            <Delete fontSize='small' />
                                        </IconButton>
                                    </Tooltip>
                                </Box>

                                {/* Bottom Info Bar */}
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            bottom: 0,
                                            left: 0,
                                            right: 0,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        backgroundColor: image.isThumbnail
                                            ? theme.palette.primary.main
                                            : 'rgba(0,0,0,0.6)',
                                            color: 'white',
                                            py: 0.5,
                                        px: 1
                                        }}
                                    >
                                    {image.isThumbnail ? (
                                        <Typography 
                                            variant='caption' 
                                            sx={{ 
                                                fontFamily: 'primaryFont',
                                                fontWeight: 'bold',
                                                fontSize: '0.65rem'
                                            }}
                                        >
                                            THUMBNAIL
                                        </Typography>
                                    ) : (
                                        <Box />
                                    )}
                                    </Box>
                            </Card>
                        ))}
                    </Box>
                </Box>
            )}

            {/* Resize Mode Menu */}
            <Menu
                anchorEl={resizeModeAnchor?.element}
                open={Boolean(resizeModeAnchor)}
                onClose={handleResizeModeClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    paper: {
                        sx: {
                            minWidth: 180,
                            mt: 0.5
                        }
                    }
                }}
            >
                <Typography
                    variant='caption'
                    sx={{
                        px: 2,
                        py: 1,
                        display: 'block',
                        fontFamily: 'primaryFont',
                        fontWeight: 600,
                        color: theme.palette.text.secondary,
                        borderBottom: `1px solid ${theme.palette.divider}`
                    }}
                >
                    Image Fit Mode
                </Typography>
                {RESIZE_MODE_OPTIONS.map((option) => {
                    const currentImage = images.find(img => img.id === resizeModeAnchor?.imageId);
                    const isSelected = (currentImage?.resizeMode || 'cover') === option.value;
                    return (
                        <MenuItem
                            key={option.value}
                            onClick={() => handleResizeModeChange(option.value)}
                            selected={isSelected}
                            sx={{ py: 1 }}
                        >
                            <ListItemIcon sx={{ color: isSelected ? theme.palette.primary.main : 'inherit' }}>
                                {option.icon}
                            </ListItemIcon>
                            <ListItemText
                                primary={option.label}
                                secondary={option.description}
                                primaryTypographyProps={{
                                    fontFamily: 'primaryFont',
                                    fontWeight: isSelected ? 600 : 400,
                                    fontSize: '0.875rem'
                                }}
                                secondaryTypographyProps={{
                                    fontFamily: 'primaryFont',
                                    fontSize: '0.7rem'
                                }}
                            />
                            {isSelected && (
                                <Check fontSize='small' sx={{ color: theme.palette.primary.main, ml: 1 }} />
                            )}
                        </MenuItem>
                    );
                })}
            </Menu>
        </Box>
    );
};

